import { zodResolver } from '@hookform/resolvers/zod';
import Save from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { FieldErrors, FormProvider, Path, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AuthGuard from '@/components/auth-guard';
import { BasicIdentitySection } from '@/components/character-form/basic-identity';
import { CustomFieldsSection } from '@/components/character-form/custom-fields-section';
import { ImageManager } from '@/components/character-form/image-manager';
import { VoiceProfileSection } from '@/components/character-form/voice-profile-section';
import { FloatingSaveButton } from '@/components/floating-save-button';
import { Page } from '@/components/page';
import {
  CharacterFormSchema,
  CharacterFormValues,
  useCharacterAccessLayer
} from '@/features/characters';
import { syncOrganizationsForCharacter } from '@/features/organizations/utils/membership-sync';
import { ORGANIZATION_HIERARCHY_FEATURE_KEY } from '@/features/organizations/utils/organization-hierarchy-feature';
import { useImageProcessor } from '@/features/shared/hooks/use-image-processor';
import { useEntities } from '@/hooks/use-entities';
import { useResponsiveValue } from '@/hooks/use-responsive-value';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { useSubscription } from '@/hooks/useSubscription';
import { Character as DomainCharacter, Organization as DomainOrganization } from '@/lib/types';
import { splitBySchemaShape } from '@/lib/utils/split-by-schema';

export const Route = createFileRoute('/characters/new')({
  component: NewCharacterPage,
});

function NewCharacterForm() {
  const { novel, getNewId, updateEntityItem, saveItem, deleteItem } = useNovelRepository();
  const novelId = novel?.id;
  const { toast } = useToast();
  const resolveResponsive = useResponsiveValue();
  const { t } = useTranslation();
  const { hasFeature } = useSubscription();
  const isGenerativeAiAllowed = hasFeature('is_generative_ai_allowed');
  const isOrganizationHierarchyEnabled = hasFeature(ORGANIZATION_HIERARCHY_FEATURE_KEY);

  const characterId = useMemo(() => getNewId(), [getNewId]);

  const {
    createCharacter: createCharacterLayer,
    character: combinedData,
    sections: characterSettings,
  } = useCharacterAccessLayer(characterId);

  const { processImages, isProcessing: isProcessingImages } = useImageProcessor();
  const { data: organizationDocs, isLoading: isLoadingOrganizations } = useEntities<DomainOrganization>('organizations', isOrganizationHierarchyEnabled);
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);

  const isProcessing = isProcessingImages;

  if (!novel.id) {
    navigate({ to: '/characters' });
    return null;
  }

  const methods = useForm<CharacterFormValues>({
    resolver: zodResolver(CharacterFormSchema),
    defaultValues: {
      name: '',
      type: 'Supporting',
      shortDescription: '',
      visualDescription: '',
      fullName: '',
      nickname: '',
      characterClass: '',
      organizationIds: [],
      images: [],
      primaryImageId: null,
      pendingFiles: [],
      voiceProfile: {
        archetype: '',
        baseStyle: {
          tone: 'formal',
          directness: 'direct',
          verbosity: 'medium',
          emotionalExpression: 'balanced',
        },
        verbalTics: [],
      },
      audit: {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    },
  });

  // Sync images and primary UUID from background updates (e.g. AI generation)
  useEffect(() => {
    if (combinedData?.images && JSON.stringify(combinedData.images) !== JSON.stringify(methods.getValues('images'))) {
      methods.setValue('images', combinedData.images as CharacterFormValues['images'], { shouldDirty: true });
    }
    if (combinedData?.primaryImageId !== undefined && combinedData.primaryImageId !== methods.getValues('primaryImageId')) {
      methods.setValue('primaryImageId', combinedData.primaryImageId, { shouldDirty: true });
    }
  }, [combinedData?.images, combinedData?.primaryImageId, methods]);

  const onSubmit = async (values: CharacterFormValues) => {
    if (!novelId || !novel?.ownerId) return;

    setIsSaving(true);
    try {
      if (isOrganizationHierarchyEnabled && !organizationDocs) {
        throw new Error(t('entity_form.common.related_records_loading_error'));
      }

      const { pendingFiles, ...characterData } = values;
      const { defined: coreData, details } = splitBySchemaShape(CharacterFormSchema, characterData);

      let imagesUpdatedArray = [...(coreData.images || [])];

      // 1. Process images via shared hook
      if (pendingFiles && pendingFiles.length > 0) {
        const processedResults = await processImages(
          pendingFiles,
          novelId,
          `characters/${characterId}`,
          novel.ownerId
        );
        imagesUpdatedArray = [...imagesUpdatedArray, ...processedResults];
      }

      const primaryImageId = coreData.primaryImageId ?? imagesUpdatedArray[0]?.id ?? null;
      const mainImage = imagesUpdatedArray.find(image => image.id === primaryImageId);
      const mainImageUrl = mainImage?.profile?.url || mainImage?.original?.url || '';
      const mainImagePath = mainImage?.profile?.path || mainImage?.original?.path || '';

      // 2. Create via Access Layer
      await createCharacterLayer(characterId, {
        ...coreData,
        id: characterId,
        details,
        images: imagesUpdatedArray,
        primaryImageId,
        mainImageUrl,
        mainImagePath,
      });

      if (isOrganizationHierarchyEnabled && organizationDocs) {
        await syncOrganizationsForCharacter({
          characterId,
          previousOrganizationIds: [],
          nextOrganizationIds: (coreData.organizationIds || []) as string[],
          organizations: organizationDocs,
          updateEntityItem,
          saveRelationship: (relationship) => saveItem('entities', 'relationships', relationship),
          deleteRelationship: (relationshipId) => deleteItem('entities', 'relationships', relationshipId),
        });
      }

      toast({
        title: t('entity_form.characters.create_success_title'),
        description: t('entity_form.characters.create_success_desc', { name: values.name }),
      });

      navigate({ to: '/characters' });
    } catch (error) {
      console.error('Submit error:', error);
      toast({
        title: t('entity_form.common.save_failed_title'),
        description: error instanceof Error ? error.message : t('entity_form.common.unexpected_error'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const onInvalid = (errors: FieldErrors<CharacterFormValues>) => {
    const errorFields = Object.keys(errors).map(key => {
      return key.charAt(0).toUpperCase() + key.slice(1);
    });

    toast({
      title: t('entity_form.common.validation_failed_title'),
      description: t('entity_form.common.validation_failed_desc', { fields: errorFields.join(', ') }),
      variant: 'destructive',
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit, onInvalid)}>
        <Page
          title={t('entity_form.characters.new_title')}
          description={t('entity_form.characters.new_desc')}
          breadcrumbs={[
            { label: t('entity_list.characters.title'), href: '/characters' },
            { label: t('entity_form.characters.breadcrumb_new') }
          ]}
          layoutMode="scrolling"
          actions={
            <Button
              type="submit"
              variant="contained"
              disabled={isSaving || (isOrganizationHierarchyEnabled && isLoadingOrganizations)}
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: '1rem' }} />}
              sx={{ px: 4, borderRadius: 2 }}
            >
              {t('entity_form.characters.save')}
            </Button>
          }
        >

          <Grid container spacing={4}>
            <Grid size={resolveResponsive({ xs: 12, lg: 8 })}>
              <Stack spacing={4}>
                <Stack spacing={4} id="main-info">
                  <BasicIdentitySection />
                  {isGenerativeAiAllowed && <VoiceProfileSection />}
                </Stack>

                <section id="custom-fields">
                  {novelId && <CustomFieldsSection novelId={novelId} sections={characterSettings} />}
                </section>
              </Stack>
            </Grid>

            <Grid size={resolveResponsive({ xs: 12, lg: 4 })}>
              <Box component="aside" sx={{ position: { lg: 'sticky' }, top: 24 }}>
                {novelId && (
                  <ImageManager
                    onUpdate={(updates) => {
                      (Object.keys(updates) as Array<keyof typeof updates>).forEach((key) => {
                        const value = updates[key];
                        if (value !== undefined) {
                          methods.setValue(key as Path<CharacterFormValues>, value, { shouldDirty: true, shouldTouch: true });
                        }
                      });
                    }}
                    isProcessing={isProcessing}
                    character={{ ...methods.getValues(), id: characterId } as DomainCharacter}
                    pendingFiles={(methods.watch('pendingFiles') as File[]) || []}
                    visualDescription={methods.watch('visualDescription')}
                    onVisualDescriptionChange={(value) => methods.setValue('visualDescription', value, { shouldDirty: true })}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Page>
        <FloatingSaveButton
          isSaving={isSaving}
          disabled={isOrganizationHierarchyEnabled && isLoadingOrganizations}
          label={t('entity_form.characters.save')}
        />
      </form>
    </FormProvider>
  );
}

function NewCharacterPage() {
  const { t } = useTranslation();
  return (
    <AuthGuard>
      <Suspense fallback={
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 3 }}>
          <CircularProgress color="primary" thickness={2} size={48} />
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.2em' }}>
            {t('entity_form.common.initializing')}
          </Typography>
        </Box>
      }>
        <NewCharacterForm />
      </Suspense>
    </AuthGuard>
  );
}
